import { Component, OnInit, ViewChild } from '@angular/core';
import { ModalController, LoadingController, AlertController, IonInput, Platform, NavParams } from '@ionic/angular';
import { AuthService } from '../../services/auth.service';
import { APIService } from '../../services/api.service';
import { Router } from '@angular/router';
import { PersonService } from '../../services/person.service';
import { CountryService } from '../../services/country.service';
import { OrganisationService } from './../../services/organisation.service';
import { OrganisationPickerPage } from './../../modals/organisation-picker/organisation-picker.page';
import { CountryPickerPage } from './../../modals/country-picker/country-picker.page';
import { ObserverService } from '../../services/observer.service';
import { PricingTierService } from '../../services/pricing-tier.service';
import { StorageService } from '../../services/storage.service';
import { RegisterService } from '../../services/register.service';
import { Capacitor } from '@capacitor/core';

@Component({
    selector: 'app-person-add',
    templateUrl: './person-add.page.html',
    styleUrls: ['./person-add.page.scss'],
})
export class PersonAddPage implements OnInit {

    @ViewChild('first_name_input', { static: false }) first_name_input: IonInput;

    spinner = null;
    loading_spinner = null;
    loading = false;
    online = true;
    input_focused = false;
    pricing_tiers = null;
    email_lists = null;
    mailing_address_country = null;
    billing_address_country = null;
    billing_address_same_as_mailing_address = false;
    modal_css_class = '';
    organisation = null;
    invoked_from = null;
    added_person_id = null;

    params = {
        pricing_tier_id: "FD5F3DDA-B23C-DF79-8676151A26F699B1",
        create_username: true
    };

    selectOptions: any = {
	    cssClass: "fullscreen-select"
    };

    // modal flags
    organisation_picker_modal_open = false;
    country_picker_modal_open = false;

    constructor(
        public modalController: ModalController,
        public loadingController: LoadingController,
        public alertController: AlertController,
        private platform: Platform,
        private authService: AuthService,
        private apiService: APIService,
        private router: Router,
        private personService: PersonService,
        private countryService: CountryService,
        private organisationService: OrganisationService,
        private navParams: NavParams,
        private observerService: ObserverService,
        private pricingTierService: PricingTierService,
        private storageService: StorageService,
        private registerService: RegisterService
    ) { }

    ngOnInit() {

        if(this.navParams.get('invoked_from')) {
            this.invoked_from = this.navParams.get('invoked_from');
        }

        if(Capacitor.isNativePlatform()) {
            this.modal_css_class = 'fullscreen';
        }

        this.loadData().then(() => { }).catch(() => { });

        this.observerService.subscribe('pricing_tier_added|person_add_modal', (pricing_tier) => {
            if(pricing_tier) {
                let pricing_tiers = this.pricing_tiers;
                pricing_tiers.push(pricing_tier);
                this.sortPricingTiers(pricing_tiers);
            }
        });

        this.observerService.subscribe('pricing_tier_deleted|person_add_modal', (pricing_tier) => {
            if(pricing_tier) {
                let pricing_tiers = this.pricing_tiers;
                for(let i = 0; i < pricing_tiers.length; i++) {
                    if(pricing_tiers[i].id === pricing_tier["id"]) {
                        pricing_tiers.splice(i, 1);
                        break;
                    }
                }
                this.sortPricingTiers(pricing_tiers);
            }
        });

        this.observerService.subscribe('pricing_tier_updated|person_add_modal', (pricing_tier) => {
            if(pricing_tier) {
                let pricing_tiers = this.pricing_tiers;
                for(let i = 0; i < pricing_tiers.length; i++) {
                    if(pricing_tiers[i].id === pricing_tier["id"]) {
                        pricing_tiers[i] = pricing_tier;
                        break;
                    }
                }
                this.sortPricingTiers(pricing_tiers);
            }
        });

        this.observerService.subscribe('pricing_tiers_changed|person_add_modal', (pricing_tiers) => {
            if(pricing_tiers) {
                this.sortPricingTiers(pricing_tiers);
            }
        });

        this.observerService.subscribe('network_status_changed|person_add_modal', (network_status) => {
            if(network_status) {
                this.online = network_status.connected;
                if(this.online) {
                    this.loadData().then(() => {} ).catch(() => { });
                }
                else {
                    this.presentAlert("Problem", "Offline", "You must be online to add a person, please check your network connection and try again.");
                }
            }
        });

    }

    // NOTE: focus input after rendering.
    ngAfterViewChecked() {
        if(!this.loading && this.online && !this.input_focused && this.first_name_input) {
            setTimeout(() => {
                this.first_name_input.setFocus();
                this.input_focused = true;
            }, 100);
        }
    }

    ngOnDestroy() {
        this.observerService.destroy("pricing_tier_added|person_add_modal");
        this.observerService.destroy("pricing_tier_deleted|person_add_modal");
        this.observerService.destroy("pricing_tier_updated|person_add_modal");
        this.observerService.destroy("pricing_tiers_changed|person_add_modal");
		this.observerService.destroy("network_status_changed|person_add_modal");
    }

    loadData() {
        return new Promise<void>((resolve, reject) => {
            this.presentLoadingSpinner("Loading pricing tiers").then(() => {
                this.pricingTierService.getPricingTiers().then((pricing_tiers) => {
                    if(pricing_tiers) {
                        this.sortPricingTiers(pricing_tiers);
                    }
                    this.loading_spinner.message = "Loading email lists";
                    this.loadEmailLists().then(() => {
                        this.loading_spinner.message = "Loading register";
                        this.storageService.get("register_id").then((register_id) => {
                            if(register_id) {
                                this.registerService.getRegister(register_id).then(async (register: any) => {
                                    if(register.location['physical_address']) {
                                        this.params['mailing_address_country_id'] = register.location.physical_address.country.id
                                        await this.loadCountry(register.location.physical_address.country.id).then((country) => {
                                            this.mailing_address_country = country;
                                        })
                                        .catch(() => { });
                                    }
                                    this.loading_spinner.dismiss();
                                    resolve();
                                })
                                .catch(() => {
                                    this.loading_spinner.dismiss();
                                    reject();
                                });
                            }
                        })
                        .catch(() => { 
                            this.loading_spinner.dismiss();
                            reject();
                        });
                    })
                    .catch(() => {
                        this.loading_spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.loading_spinner.dismiss();
                    reject();
                });
            });
        });
    }

    loadEmailLists() {
        return new Promise<void>((resolve, reject) => {
            this.email_lists = null;
            this.authService.isLoggedIn().then(() => {
                this.apiService.getEmailLists().then((email_lists) => {
                    if(email_lists) {
                        this.email_lists = email_lists;
                    }
                    resolve();
                })
                .catch((error) => {
                    if(error.status === 400) {
                        this.presentAlert('Problem', 'Unknown error', 'Unfortunately something went wrong when trying to retrieve a list of email lists.');
                    } 
                    else if(error.status === 403) {
                        this.presentAlert('Problem', 'Insufficient permissions', "You don't have permission to view a list of email lists.");
                    } 
                    else if(error.status === 404 || error.status === 0) {
                        this.presentAlert('Problem', 'Unavailable', "Unfortunately we're unable to retrieve a list of email lists at this time.");
                    }
                    reject();
                });
            })
            .catch(() => {
                this.router.navigate(['switch-account'], { replaceUrl: true }).then(() => {
                    window.location.reload();
                });
            });
        });
    }

    loadCountry(country_id) {
        return new Promise((resolve, reject) => {
            this.countryService.getCountry(country_id).then((country: any) => {
                if(country) {
                    resolve(country);
                }
            })
            .catch(() => {
                reject();
            });
        });
    }

    close() {
        this.modalController.dismiss();
    }

    sortPricingTiers(pricing_tiers) {
        pricing_tiers.sort((a, b) => {
            if(a['order'] < b['order']) { return -1; }
            if(a['order'] > b['order']) { return 1; }
            return 0;
        });
        this.pricing_tiers = pricing_tiers;
    }

    add() {

        let validated = true;
        let email_address_regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        if(this.params['first_name'] == null || this.params['first_name'].trim() === "") {
            this.presentAlert("Problem", "First name required", "");
            validated = false;
        }
        else if(this.params['last_name'] == null || this.params['last_name'].trim() === "") {
            this.presentAlert("Problem", "Last name required", "");
            validated = false;
        }
        else if(this.params['email_address'] && !email_address_regex.test(String(this.params['email_address']).toLowerCase())) {
            this.presentAlert("Problem", "Invalid email address", "");
            validated = false;
        }

        if(validated) {
            this.addPerson().then(() => {

                if(this.invoked_from == "cart_customer_modal" || this.invoked_from == "person_picker_modal") {
                    this.modalController.dismiss({person_id: this.added_person_id});
                }
                else {
                    this.modalController.dismiss();
                }

            });
        }
    }

    addPerson() {
        return new Promise<void>((resolve, reject) => {
            this.presentSpinner("Adding person").then(() => {
                this.authService.isLoggedIn().then(() => {
                    this.apiService.addPerson(this.params).then((person) => {
                        if(person) {
                            this.added_person_id = person["id"];
                            this.personService.addPerson(person).then(() => {}).catch(() => {});
                        }
                        this.spinner.dismiss();
                        resolve();
                    })
                    .catch((error) => {
                        if(error) {
                            if(error.status === 400) {
                                this.presentAlert("Problem", "Unknown error", "Unfortunately something went wrong when trying to add this person.");
                            } 
                            else if(error.status === 403) {
                                this.presentAlert("Problem", "Insufficient permissions", "You don't have permission to add this person.");
                            } 
                            else if(error.status === 404 || error.status === 0) {
                                this.presentAlert("Problem", "Unavailable", "Unfortunately we're unable to add this person at this time.");
                            }
                        }
                        this.spinner.dismiss();
                        reject();
                    });
                })
                .catch(() => {
                    this.router.navigate(["switch-account"], { replaceUrl: true }).then(() => {
                        window.location.reload();
                    });
                });
            });
        });
    }

    clearOrganisation() {
        if(this.params['organisation_id']) {
            this.params['organisation_id'] = null;
        }
    }

    async presentOrganisationPickerModal() {
        if(!this.organisation_picker_modal_open) {
            this.organisation_picker_modal_open = true;
            const modal = await this.modalController.create({
                component: OrganisationPickerPage,
                cssClass: this.modal_css_class,
                componentProps: {
                    organisation_id: (this.params['organisation_id'] ? this.params['organisation_id'] : null)
                }
            });
            modal.onDidDismiss().then((data) => {
                this.organisation_picker_modal_open = false;
                if(data.data) {
                    if(data.data['picker_action'] == "organisation_deleted") {
                        this.params["organisation_id"] = "";
                        this.organisation = null;
                    }
                    else {
                        this.params["organisation_id"] = data.data['organisation_id'];
                        this.organisationService.getOrganisation(data.data['organisation_id'],{}).then((organisation) => {
                            if(organisation) {
                                this.organisation = organisation;
                            }
                        })
                        .catch(() => { });
                    }
                }
            });
            return await modal.present();
        }
        else {
            return null;
        }
    }

async presentMailingAddressCountryPickerModal() {
        if(!this.country_picker_modal_open) {
            this.country_picker_modal_open = true;
            const modal = await this.modalController.create({
                component: CountryPickerPage,
                cssClass: this.modal_css_class,
                componentProps: {
                    country_id: (this.params['mailing_address_country_id'] ? this.params['mailing_address_country_id'] : null)
                }
            });
            modal.onDidDismiss().then((data) => {
                this.country_picker_modal_open = false;
                if(data.data) {
                    if(data.data === "country_deleted") {
                        this.params["mailing_address_country_id"] = "";
                        this.mailing_address_country = null;
                    }
                    else {
                        this.params["mailing_address_country_id"] = data.data['country_id'];
                        this.countryService.getCountry(data.data['country_id']).then((country) => {
                            if(country) {
                                this.mailing_address_country = country;
                            }
                        })
                        .catch(() => { });
                    }
                }
            });
            return await modal.present();
        }
        else {
            return null;
        }
    }

    async presentBillingAddressCountryPickerModal() {
        if(!this.country_picker_modal_open) {
            this.country_picker_modal_open = true;
            const modal = await this.modalController.create({
                component: CountryPickerPage,
                cssClass: this.modal_css_class,
                componentProps: {
                    country_id: (this.params['billing_address_country_id'] ? this.params['billing_address_country_id'] : null)
                }
            });
            modal.onDidDismiss().then((data) => {
                this.country_picker_modal_open = false;
                if(data.data) {
                    if(data.data === "country_deleted") {
                        this.params["billing_address_country_id"] = "";
                        this.billing_address_country = null;
                    }
                    else {
                        this.params["billing_address_country_id"] = data.data['country_id'];
                        this.countryService.getCountry(data.data['country_id']).then((country) => {
                            if(country) {
                                this.billing_address_country = country;
                            }
                        })
                        .catch(() => { });
                    }
                }
            });
            return await modal.present();
        }
        else {
            return null;
        }
    }

    prefillBillingAddress() {
        if(this.billing_address_same_as_mailing_address) {
            if(this.params['mailing_address_line_1']) {
                this.params['billing_address_line_1'] = this.params['mailing_address_line_1'];
            }
            if(this.params['mailing_address_line_2']) {
                this.params['billing_address_line_2'] = this.params['mailing_address_line_2'];
            }
            if(this.params['mailing_address_locality']) {
                this.params['billing_address_locality'] = this.params['mailing_address_locality'];
            }
            if(this.params['mailing_address_subdivision']) {
                this.params['billing_address_subdivision'] = this.params['mailing_address_subdivision'];
            }
            if(this.params['mailing_address_postal_code']) {
                this.params['billing_address_postal_code'] = this.params['mailing_address_postal_code'];
            }
            if(this.params['mailing_address_country_id']) {
                this.params['billing_address_country_id'] = this.params['mailing_address_country_id'];
                this.billing_address_country = this.mailing_address_country;
            }
        }
        else {
            delete this.params['billing_address_line_1'];
            delete this.params['billing_address_line_2'];
            delete this.params['billing_address_locality'];
            delete this.params['billing_address_subdivision'];
            delete this.params['billing_address_postal_code'];
            delete this.params['billing_address_country_id'];
            this.billing_address_country = null;
        }
    }

    async presentAlert(header, sub_header, message) {
        const alert = await this.alertController.create({
            header: header,
            subHeader: sub_header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

    async presentSpinner(message) {
        this.spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        await this.spinner.present();
    }

    async presentLoadingSpinner(message) {
        this.loading = true;
        this.loading_spinner = await this.loadingController.create({
            message: message,
            showBackdrop: false,
            cssClass: 'loading-spinner'
        });
        this.loading_spinner.onDidDismiss().then(() => {
            this.loading = false;
        });
        await this.loading_spinner.present();
    }

}